import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Sparkles, Music2, RefreshCw, Loader2, Lightbulb } from "lucide-react";

const ContentIdeas = ({ platform }) => {
  const [ideas, setIdeas] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/content/ideas${platform ? `?platform=${platform}` : ""}`)
      .then((r) => setIdeas(r.data.ideas))
      .catch(() => setIdeas(null))
      .finally(() => setLoading(false));
  }, [platform]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="rounded-xl border border-border/50 bg-card p-6" data-testid="content-ideas">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4.5 h-4.5 text-primary" style={{ width: 18, height: 18 }} />
          <h3 className="font-heading text-lg font-semibold">AI Content Ideas</h3>
        </div>
        <Button variant="ghost" size="sm" onClick={load} disabled={loading} className="text-muted-foreground hover:text-foreground" data-testid="refresh-ideas-btn">
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-10"><Loader2 className="w-6 h-6 text-primary animate-spin" /></div>
      ) : !ideas ? (
        <p className="text-sm text-muted-foreground py-6 text-center">No ideas right now — try refreshing.</p>
      ) : (
        <div className="space-y-6">
          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground mb-3">Trending sound directions</div>
            <div className="space-y-2">
              {(ideas.sounds || []).map((s, i) => (
                <div key={i} className="flex items-start gap-3 rounded-lg bg-white/5 border border-white/5 p-3" data-testid={`sound-idea-${i}`}>
                  <div className="w-8 h-8 rounded-lg bg-secondary/10 text-secondary flex items-center justify-center shrink-0"><Music2 className="w-4 h-4" /></div>
                  <div>
                    <div className="text-sm font-semibold">{s.title}</div>
                    <div className="text-xs text-muted-foreground">{s.why}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground mb-3">Post ideas</div>
            <div className="grid sm:grid-cols-2 gap-3">
              {(ideas.posts || []).map((p, i) => (
                <div key={i} className="rounded-lg border border-white/10 p-4" data-testid={`post-idea-${i}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <Lightbulb className="w-4 h-4 text-primary shrink-0" />
                    <div className="text-sm font-semibold leading-tight">{p.hook}</div>
                  </div>
                  <div className="text-xs text-muted-foreground">{p.format}</div>
                  <div className="mt-2 inline-block text-[11px] font-medium px-2 py-0.5 rounded-full bg-primary/10 text-primary">{p.cta}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContentIdeas;
